import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AUTH_ROUTES } from '../../auth/constants/auth-routes.constants';
import { AuthService } from '../../auth/services/auth/auth.service';
import { HTTP_STATUS_CODES } from '../../shared/constants/http-codes';
import { AuthTokenStore } from '../../auth/services/auth-token/auth-token.store';
import { AppRouteNavigationService } from '../services/app-route-navigation.service';
import { UpsellService } from '../services/upsell.service';
import { ISubscriptionLimitError } from '../../views/settings/interfaces/subscription-limit-error.interface';

export const AuthInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const appRouteNavigation = inject(AppRouteNavigationService);
  const upsellService = inject(UpsellService);

  const tokenStore = inject(AuthTokenStore);
  const token = tokenStore.readToken();

  if (token) {
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      const isTeamMembersInviteRequest = request.url.includes('/entity/team-members/invite');
      const isAuthSessionValidationRequest = request.url.includes('/auth/me');

      if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED && !request.url.includes('/auth/login')) {
        if (!isAuthSessionValidationRequest) {
          return throwError(() => error);
        }
        const currentUrl = router.url.trim();
        const isOfferClaimFlow =
          currentUrl.includes('/sponsor/offers/claim') || /^\/offers\/[^/]+/.test(currentUrl);
        const isSponsorOnboardingFlow = currentUrl.includes('/onboarding/sponsor');
        if (isOfferClaimFlow || isSponsorOnboardingFlow) {
          return throwError(() => error);
        }
        const safeRedirectUrl = authService.sanitizePostLoginRedirect(currentUrl);

        authService.logout();
        if (safeRedirectUrl) {
          authService.setPostLoginRedirect(safeRedirectUrl);
          void appRouteNavigation.navigate(AUTH_ROUTES.login, {
            queryParams: { redirectTo: safeRedirectUrl },
          });
        } else {
          void appRouteNavigation.navigate(AUTH_ROUTES.login);
        }
      }

      if (error.status === HTTP_STATUS_CODES.PAYMENT_REQUIRED && !isTeamMembersInviteRequest) {
        const errorBody = error.error as {
          error?: ISubscriptionLimitError;
          message?: string;
        };
        const code = errorBody?.error?.code || errorBody?.message;
        if (code === 'subscriptionRequired' || code === 'subscriptionLimitExceeded') {
          upsellService.trigger({
            code,
            limitType: errorBody.error?.limit_type,
            currentUsage: errorBody.error?.current_usage,
            limit: errorBody.error?.limit,
          });
        }
      }
      return throwError(() => error);
    })
  );
};
